import type { ServerLegalFacts } from '@calling/shared';
import { useEffect, useState } from 'react';
import { api } from './api';
import { joinList } from './format';

/**
 * A way to sign in the server offers. Apple's also says whether the iPhone
 * app uses the system's Sign in with Apple sheet (`iosNative`; otherwise
 * Apple's page, as on Android), and whether it is the stand-in that runs on a
 * developer's machine (`sandbox`).
 */
export type Provider = { id: 'google' | 'microsoft' | 'apple'; label: string; iosNative?: boolean; sandbox?: boolean };

export interface ServerInfo {
  providers: Provider[];
  /** False when the server only logs email (no SMTP configured). */
  emailEnabled: boolean;
  /** Where people write for help, if the operator set one. */
  supportEmail?: string | null;
  /** People can sign in with their email address and a password too. */
  password?: boolean;
  /** A developer's own server without SMTP: the emails it would have sent are at /dev/mailbox. */
  devMailbox?: boolean;
  /** The apps' pages on the App Store and Google Play, for the badges on the website; null while an app is not live there. */
  stores?: { appStore: string | null; googlePlay: string | null };
  /** How many days database backups are kept, for the deletion pages. */
  retention?: { backupDays: number | null };
  /** What the privacy policy and the terms say about who runs the service and how (shared/src/legal.ts). */
  legal?: ServerLegalFacts;
  /** The oldest builds of the apps that still work with this server; 0 for any. */
  minBuild?: { ios: number; android: number };
}

let serverInfo: Promise<ServerInfo> | null = null;
/** The answer once it has come: a page opened after that shows it straight away. */
let known: ServerInfo | null = null;

/** What the server offers: asked for once, as the answer does not change while the app is open. */
export function loadServerInfo(): Promise<ServerInfo> {
  serverInfo ??= api.request<ServerInfo>('/api/auth/providers', { auth: 'none' }).then(
    (info) => (known = info),
    (err: unknown) => {
      serverInfo = null;
      throw err;
    },
  );
  return serverInfo;
}

export function useServerInfo(): { info: ServerInfo | null; failed: boolean } {
  const [info, setInfo] = useState<ServerInfo | null>(known);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadServerInfo()
      .then((res) => !cancelled && setInfo(res))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);
  return { info, failed };
}

/** The ways to sign in that this server offers; null until it has said. */
export function useProviders(): { providers: Provider[] | null; failed: boolean } {
  const { info, failed } = useServerInfo();
  return { providers: info?.providers ?? null, failed };
}

/**
 * The ways to sign in, named for a sentence: "Google", "Google or
 * Microsoft", "Google, Microsoft or Apple". null while they load, or when
 * there are none: the sentence then names none, rather than one this server
 * does not offer.
 */
export function providerNames(providers: readonly Provider[] | null | undefined, joiner: 'or' | 'and' = 'or'): string | null {
  return providers?.length ? joinList(providers.map((p) => p.label), joiner) : null;
}

/** The ways to sign in on this server, named for a sentence (see providerNames). */
export const useProviderNames = (): string | null => providerNames(useProviders().providers);

/** Whether this server offers signing in with an email address and a password; null until it has said. */
export function usePasswordSignIn(): boolean | null {
  const { info, failed } = useServerInfo();
  return info ? info.password === true : failed ? false : null;
}

/**
 * Every way to sign in, named for a sentence: "Google", "Google or your
 * email address", "your email address". null while they load, or when there
 * are none.
 */
export function signInWays(providers: readonly Provider[] | null | undefined, password: boolean | null | undefined, joiner: 'or' | 'and' = 'or'): string | null {
  const ways = [...(providers ?? []).map((p) => p.label), ...(password ? ['your email address'] : [])];
  return ways.length === 0 ? null : joinList(ways, joiner);
}

/** The ways to sign in on this server, the email address included where it is offered (see signInWays). */
export function useSignInWays(): string | null {
  const { info } = useServerInfo();
  return signInWays(info?.providers, info?.password);
}
